import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { Document } from "../../types";
import { EntityStatus, GuideSourceFormat } from "../../types";
import { getTenantId } from "../../utils/jwtUtils";
import DocumentMarkdownPreview from "./DocumentMarkdownPreview";
import {
  ARCHITECTURE_ARTIFACT_LABELS,
  ARCHITECTURE_ARTIFACT_TYPES,
  ARCHITECTURE_LIFECYCLE_PHASES,
  ARCHITECTURE_PHASE_LABELS,
  ARCHITECTURE_STANDARD_ENTITY_CLASS,
  architectureClassificationsJson,
  architectureStandardName,
  parseArchitectureClassifications,
  type ArchitectureArtifactType,
  type ArchitectureLifecyclePhase,
} from "../../utils/architectureStandards";

interface EditArchitectureStandardDialogProps {
  document: Document | null;
  open: boolean;
  creating: boolean;
  onClose: () => void;
  onSaved: (updated: Document) => void;
}

const EditArchitectureStandardDialog: React.FC<
  EditArchitectureStandardDialogProps
> = ({ document: doc, open, creating, onClose, onSaved }) => {
  const parsed = parseArchitectureClassifications(doc?.classifications);
  const [artifactType, setArtifactType] = useState<ArchitectureArtifactType>(
    (parsed.artifactType as ArchitectureArtifactType) ?? "microservice",
  );
  const [lifecyclePhase, setLifecyclePhase] =
    useState<ArchitectureLifecyclePhase>(
      (parsed.lifecyclePhase as ArchitectureLifecyclePhase) ?? "design",
    );
  const [status, setStatus] = useState<EntityStatus>(
    doc?.status ?? EntityStatus.DRAFT,
  );
  const [content, setContent] = useState(doc?.content ?? "");
  const [description, setDescription] = useState(doc?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    const next = parseArchitectureClassifications(doc?.classifications);
    setArtifactType(
      (next.artifactType as ArchitectureArtifactType) ?? "microservice",
    );
    setLifecyclePhase(
      (next.lifecyclePhase as ArchitectureLifecyclePhase) ?? "design",
    );
    setStatus(doc?.status ?? EntityStatus.DRAFT);
    setContent(doc?.content ?? "");
    setDescription(doc?.description ?? "");
    setError(null);
    setPreview(false);
  }, [doc, open]);

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("Tenant is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const name = architectureStandardName(artifactType, lifecyclePhase);
    const classifications = architectureClassificationsJson(
      artifactType,
      lifecyclePhase,
    );
    const tags = [
      ARCHITECTURE_STANDARD_ENTITY_CLASS,
      artifactType,
      lifecyclePhase,
    ];
    try {
      if (creating || !doc) {
        const created = await apiService.createDocument({
          tenantId,
          name,
          description: description.trim() || undefined,
          status,
          tags,
          entityClass: ARCHITECTURE_STANDARD_ENTITY_CLASS,
          classifications,
          content,
          sourceFormat: GuideSourceFormat.MARKDOWN,
        });
        onSaved(created);
      } else {
        const updated = await apiService.updateDocument(doc.uuid, {
          name,
          description: description.trim() || undefined,
          status,
          tags,
          entityClass: ARCHITECTURE_STANDARD_ENTITY_CLASS,
          classifications,
          content,
        });
        onSaved(updated);
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save standard");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {creating ? "New architecture standard" : "Edit architecture standard"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Artifact type"
            size="small"
            value={artifactType}
            onChange={(e) =>
              setArtifactType(e.target.value as ArchitectureArtifactType)
            }
            disabled={!creating}
          >
            {ARCHITECTURE_ARTIFACT_TYPES.map((id) => (
              <MenuItem key={id} value={id}>
                {ARCHITECTURE_ARTIFACT_LABELS[id]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Lifecycle phase"
            size="small"
            value={lifecyclePhase}
            onChange={(e) =>
              setLifecyclePhase(e.target.value as ArchitectureLifecyclePhase)
            }
            disabled={!creating}
          >
            {ARCHITECTURE_LIFECYCLE_PHASES.map((id) => (
              <MenuItem key={id} value={id}>
                {ARCHITECTURE_PHASE_LABELS[id]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value as EntityStatus)}
          >
            {Object.values(EntityStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Purpose / description"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {preview ? (
            <DocumentMarkdownPreview content={content} />
          ) : (
            <TextField
              label="Markdown body"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              multiline
              minRows={12}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreview((v) => !v)}>
          {preview ? "Edit markdown" : "Preview"}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={handleSave}>
          {creating ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditArchitectureStandardDialog;
